import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { showSuccess, showError } from '@/lib/swal';
import { generateTOTPSecret, verifyTOTPCode, generateBackupCodes } from '@/lib/totp';
import { Copy, Check, Shield } from 'lucide-react';

interface TwoFactorSetupProps {
  userEmail: string;
  onComplete: (secret: string, backupCodes: string[]) => void;
  onCancel: () => void;
}

export default function TwoFactorSetup({ userEmail, onComplete, onCancel }: TwoFactorSetupProps) {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'setup' | 'verify' | 'backup'>('setup');
  const [loading, setLoading] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);

  useEffect(() => {
    const { secret: newSecret, uri } = generateTOTPSecret(userEmail);
    setSecret(newSecret);

    QRCode.toDataURL(uri, { width: 256 })
      .then(url => setQrCodeUrl(url))
      .catch(err => console.error('Error generating QR code:', err));

    setBackupCodes(generateBackupCodes(10));
  }, [userEmail]);

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
      showSuccess('Secret copied to clipboard');
    } catch (error) {
      showError('Failed to copy secret');
    }
  };

  const handleCopyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setBackupCodesCopied(true);
      setTimeout(() => setBackupCodesCopied(false), 2000);
      showSuccess('Backup codes copied to clipboard');
    } catch (error) {
      showError('Failed to copy backup codes');
    }
  };

  const handleVerify = () => {
    if (!verificationCode) {
      showError('Please enter the verification code');
      return;
    }

    setLoading(true);
    
    // Verify the code
    const isValid = verifyTOTPCode(secret, verificationCode);
    
    if (isValid) {
      setStep('backup');
    } else {
      showError('Invalid verification code. Please try again.');
    }
    
    setLoading(false);
  };

  const handleComplete = () => {
    onComplete(secret, backupCodes);
  };

  if (step === 'setup') {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <div className="text-center mb-4">
            <img 
              src="/logo.png" 
              alt="Dining Steward Logo" 
              className="h-10 w-10 mx-auto mb-2"
            />
            <span className="text-lg font-semibold text-navy">Dining Steward</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Enable Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* QR Code */}
          <div className="flex flex-col items-center space-y-4">
            {qrCodeUrl && (
              <div className="p-4 bg-white border rounded-lg">
                <img src={qrCodeUrl} alt="2FA QR Code" className="w-64 h-64" />
              </div>
            )}
          </div>

          {/* Manual Entry */}
          <div className="space-y-2">
            <Label>Can't scan the QR code? Enter this key manually:</Label>
            <div className="flex gap-2">
              <Input
                value={secret}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopySecret}
                title="Copy secret"
              >
                {secretCopied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Enter this code in your authenticator app if you can't scan the QR code
            </p>
          </div>

          {/* Instructions */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Instructions:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>Open your authenticator app (Google Authenticator, Authy, etc.)</li>
              <li>Scan the QR code above or enter the key manually</li>
              <li>Click "Next" to verify your setup</li>
            </ol>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={() => setStep('verify')}>
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'verify') {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <div className="text-center mb-4">
            <img 
              src="/logo.png" 
              alt="Dining Steward Logo" 
              className="h-10 w-10 mx-auto mb-2"
            />
            <span className="text-lg font-semibold text-navy">Dining Steward</span>
          </div>
          <CardTitle>Verify Your Authenticator</CardTitle>
          <CardDescription>
            Enter the 6-digit code from your authenticator app to verify the setup
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="verificationCode">Verification Code</Label>
            <Input
              id="verificationCode"
              type="text"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono"
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-center">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setStep('setup')}>
              Back
            </Button>
            <Button onClick={handleVerify} disabled={loading || verificationCode.length !== 6}>
              {loading ? 'Verifying...' : 'Verify'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Backup codes step
  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <div className="text-center mb-4">
          <img 
            src="/src/images/logo.png" 
            alt="Dining Steward Logo" 
            className="h-10 w-10 mx-auto mb-2"
          />
          <span className="text-lg font-semibold text-navy">Dining Steward</span>
        </div>
        <CardTitle>Save Your Backup Codes</CardTitle>
        <CardDescription>
          Store these codes in a safe place. You can use them to access your account if you lose your authenticator device.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
          <p className="text-sm text-yellow-800">
            <strong>Important:</strong> Each backup code can only be used once. Store them securely and don't share them with anyone.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <Label>Your Backup Codes:</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyBackupCodes}
            >
              {backupCodesCopied ? (
                <>
                  <Check className="h-4 w-4 mr-1 text-green-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy All
                </>
              )}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 border rounded-lg font-mono text-sm">
            {backupCodes.map((code, index) => (
              <div key={index} className="p-2 bg-white border rounded text-center">
                {code}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-green-50 border border-green-200 p-4">
          <p className="text-sm text-green-800">
            Two-factor authentication will be enabled once you click "Complete Setup" below.
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleComplete}>
            Complete Setup
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

